import { useCallback, useEffect, useState } from 'react';

/**
 * Altura que sobra da janela para um elemento, do topo dele até a base.
 *
 * T19 (29/08) pede **um** card de altura fixa que vá até quase a base da
 * viewport. `calc(100vh - <constante>)` não resolve: o que fica acima do card —
 * topbar, recuo da página, `PageHeader` com as pílulas, que **quebram em duas
 * linhas** em janela estreita, e o banner de erro quando existe — não tem altura
 * fixa. Um número escrito à mão acerta numa largura e erra nas outras, e o erro
 * aparece como página rolando (a mais) ou faixa morta na base (a menos).
 *
 * Então mede-se. O valor sai como número e a tela o publica em `--am-altura`,
 * não em `height` direto: assim a media query do empilhamento (< 1000 px) ainda
 * consegue devolver a altura para `auto` — um `style` inline venceria dela.
 *
 * Quem dispara a remedição é `ResizeObserver` (no próprio elemento e no `<main>`
 * da página, que é quem muda de altura quando o cabeçalho quebra) mais o
 * `resize` da janela. Deliberadamente **não** é a cada render: a tela redesenha
 * a cada hover de nó e a cada passo de arrasto, e `getBoundingClientRect` ali
 * forçaria um reflow por evento de mouse.
 */

/** Piso: abaixo disto o mapa deixa de ser legível e a página volta a rolar. */
const HEIGHT_MIN = 420;
/** Folga até a base quando a página não declara recuo próprio. */
const RECUO_MIN = 16;
/** Diferença que não vale um novo render (arredondamento de zoom do navegador). */
const RUIDO = 2;

export function useAvailableHeight(minimo: number = HEIGHT_MIN) {
  /*
   * O elemento vem por **ref de callback guardada em estado**, e não por
   * `useRef`: em T19 o card só é montado depois que a leitura do mapa volta, e
   * com `useRef` o efeito já teria rodado (com `ref.current` nulo), sem nada
   * que o fizesse rodar de novo quando o card aparecesse — a altura ficava no
   * valor de partida do CSS e a página rolava 22 px. Guardado em estado, montar
   * o card reexecuta o efeito.
   */
  const [elemento, setElemento] = useState<HTMLDivElement | null>(null);
  const [altura, setAltura] = useState<number | null>(null);

  const medir = useCallback(() => {
    if (!elemento) return;
    // O recuo de baixo da página (`.page` do AppShell) fica **abaixo** do card:
    // ignorá-lo faz o card terminar na base e a página rolar por causa dele.
    const page = elemento.closest('main');
    const recuo = page ? Number.parseFloat(getComputedStyle(page).paddingBottom) : Number.NaN;
    const slack = Number.isFinite(recuo) ? Math.max(recuo, RECUO_MIN) : RECUO_MIN;
    const disponivel = window.innerHeight - elemento.getBoundingClientRect().top - slack;
    const next = Math.round(Math.max(minimo, disponivel));
    setAltura((current) => (current !== null && Math.abs(current - next) < RUIDO ? current : next));
  }, [elemento, minimo]);

  useEffect(() => {
    medir();
    window.addEventListener('resize', medir);
    // jsdom não implementa `ResizeObserver`; sem ele fica a medição inicial e o
    // `resize`, que é o bastante para o teste (que não afirma altura nenhuma).
    const observador = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(medir);
    if (observador && elemento) {
      observador.observe(elemento);
      const page = elemento.closest('main');
      if (page) observador.observe(page);
    }
    return () => {
      window.removeEventListener('resize', medir);
      observador?.disconnect();
    };
  }, [elemento, medir]);

  return { ref: setElemento, altura };
}
